import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { useQueryClient } from "@tanstack/react-query";
import { userPool } from "@/config/cognito";
import { api, setTokenGetter } from "@/services/api";
import { setChatStreamTokenGetter } from "@/features/chat/chatApi";
import { setGenerationTokenGetter } from "@/features/draft/generationApi";
import { abortAllStreams } from "@/features/chat/chatStreamStore";

// Creates the user in the project service on first sign-in (it auto-provisions on any call).
const provisionUser = () => api.get("/api/project-service/users/me");

interface AuthUser {
  sub: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export interface NewPasswordChallenge {
  cognitoUser: CognitoUser;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ challenge: NewPasswordChallenge } | void>;
  completeNewPassword: (cognitoUser: CognitoUser, newPassword: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function extractUserFromSession(session: CognitoUserSession): AuthUser {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  const email = payload.email || "";
  const givenName = payload.given_name || "";
  const familyName = payload.family_name || "";

  // Fallback to email-derived name when Cognito name attributes aren't set
  let firstName = givenName;
  let lastName = familyName;
  if (!firstName && !lastName && email) {
    const localPart = email.split("@")[0];
    const parts = localPart.split(/[._-]/);
    firstName = parts[0]?.charAt(0).toUpperCase() + (parts[0]?.slice(1) || "");
    lastName = parts.length > 1
      ? parts[1]?.charAt(0).toUpperCase() + (parts[1]?.slice(1) || "")
      : "";
  }

  return {
    sub: payload.sub,
    email,
    firstName,
    lastName: lastName || email,
    role: (payload["cognito:groups"] || []).includes("admin") ? "Admin" : "User",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const getAccessToken = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        resolve(null);
        return;
      }
      cognitoUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            setUser(null);
            resolve(null);
            return;
          }
          resolve(session.getIdToken().getJwtToken());
        }
      );
    });
  }, []);

  // Register the token getter for the API module
  useEffect(() => {
    setTokenGetter(getAccessToken);
    setChatStreamTokenGetter(getAccessToken);
    setGenerationTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Check for existing session on mount
  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (!err && session && session.isValid()) {
            setUser(extractUserFromSession(session));
            provisionUser().catch(() => {});
          }
          setIsLoading(false);
        }
      );
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(
    (email: string, password: string): Promise<{ challenge: NewPasswordChallenge } | void> => {
      return new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });
        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session: CognitoUserSession) => {
            setUser(extractUserFromSession(session));
            provisionUser().catch(() => {});
            resolve();
          },
          onFailure: (err: Error) => {
            reject(err);
          },
          newPasswordRequired: () => {
            resolve({ challenge: { cognitoUser } });
          },
        });
      });
    },
    []
  );

  const completeNewPassword = useCallback(
    (cognitoUser: CognitoUser, newPassword: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
          onSuccess: (session: CognitoUserSession) => {
            setUser(extractUserFromSession(session));
            provisionUser().catch(() => {});
            resolve();
          },
          onFailure: (err: Error) => {
            reject(err);
          },
        });
      });
    },
    []
  );

  const logout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    setUser(null);
    // Live chat streams hold the old token; stop them before it is torn down.
    abortAllStreams();
    queryClient.clear();
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        completeNewPassword,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
